/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2, breakpoints } from '@coursera/cds-core';

import FloatToPercent from 'bundles/primitive-formatting/components/FloatToPercent';

import _t from 'i18n!nls/reviews-common';

type Props = {
  countsByStar: {
    [starValue: string]: number;
  };
  totalReviewCount: number;
};

const GOLD = 'var(--cds-color-yellow-300)';
const GREYISH = 'var(--cds-color-grey-25)';

const styles = {
  barGraphContainer: css`
    width: 50%;
    list-style-type: none;
    padding: 0;
    ${breakpoints.down('sm')} {
      width: 100%;
    }
  `,
  graphRow: css`
    display: flex;
    align-items: center;
    margin-top: var(--cds-spacing-50);
  `,
  starBarLabel: css`
    flex: 0 0 50px;
    margin-right: var(--cds-spacing-100);
  `,
  barGraphBar: css`
    flex: 0 0 170px;
    height: var(--cds-spacing-150);
    position: relative;
    margin-right: var(--cds-spacing-200);
    margin-top: var(--cds-spacing-50);
    ${breakpoints.down('sm')} {
      flex-shrink: 0.9;
    }
  `,
  numRatings: css`
    flex: 0 0 var(--cds-spacing-1400);
    margin-top: -2px;
    margin-left: -5px;
  `,
  // Ref: https://codepen.io/jedtrow/pen/YPrqKY
  greyBar: css({
    content: '""',
    top: 0,
    left: 0,
    width: '100%',
    position: 'absolute',
    height: '10px',
    background: GREYISH,
  }),
  goldBar: (maxWidthAsPercentage: string) =>
    css({
      content: '""',
      top: 0,
      left: 0,
      position: 'absolute',
      height: '10px',
      background: GOLD,
      display: 'block',
      width: maxWidthAsPercentage,
    }),
};

/**
 * A component that displays a bar graph of review ratings.
 */
const ReviewsRatingBarGraph = ({ countsByStar, totalReviewCount }: Props) => {
  if (!countsByStar || !totalReviewCount) {
    return null;
  }

  const barGraphs = Object.entries(countsByStar)
    .reverse()
    .map(([starValue, starCount]) => {
      const starCountNum = Number(starCount);
      const percentageAsDecimal = starCountNum / totalReviewCount;
      const starPercentage = `${(percentageAsDecimal * 100).toFixed(2)}%`;
      const starText = starValue === '1' ? _t('1 star') : _t('#{starValue} stars', { starValue });

      return (
        <li key={`${starValue}-star`} css={styles.graphRow}>
          <div css={styles.starBarLabel}>
            <Typography2 component="span" variant="bodyPrimary">
              {starText}
            </Typography2>
          </div>
          <div css={styles.barGraphBar}>
            <div css={styles.greyBar}>
              <div css={styles.goldBar(starPercentage)} />
            </div>
          </div>
          <div css={styles.numRatings}>
            <Typography2 component="span" variant="bodyPrimary">
              <FloatToPercent value={percentageAsDecimal} />
            </Typography2>
          </div>
        </li>
      );
    });

  return (
    <div data-unit="reviews-bar-graph">
      <ul css={styles.barGraphContainer}>{barGraphs}</ul>
    </div>
  );
};

export default ReviewsRatingBarGraph;
